/**
 * Классика трейдинга: стратегии, которые знают по именам.
 *
 * Их ищут по названию — «Черепахи», «RSI-2», «Три экрана», — и
 * запись каждой здесь честная: в индикатор входит правило входа, а про
 * выход (у Черепах — противоположный экстремум, у Коннорса — возврат
 * выше средней) сказано в совете: выход задаётся в боте, а не здесь.
 */
import { above, adx, atr, bb, below, breakOut, close, crossDown, crossUp, ema, ind, macdLine, macdSignal, mirror, number, onFrame, previous, rsiOf, rvol, scaled, sma, spec, stochK, weight, } from './kit.js';
export const CLASSIC_TEMPLATES = [
    {
        id: 'turtle-20',
        name: 'Черепахи · 20 дней',
        icon: '🐢',
        category: 'classic',
        popular: true,
        hint: 'Легендарная система Ричарда Денниса: вход на пробое максимума или минимума последних 20 баров.',
        steps: ['Пробит экстремум 20 баров', 'Тренд набирает силу', 'Объём'],
        keywords: ['черепахи', 'turtle', 'деннис', 'дончиан', 'donchian', 'пробой'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '1D',
            idea: 'В 1983 году Деннис научил новичков торговать одним правилом: покупать новый максимум 20 дней, продавать новый минимум.',
            why: 'Большие тренды начинаются с пробоя; система ловит их все, а мелкие убытки на ложных пробоях окупаются редкими крупными движениями.',
            fails: 'Ложных пробоев много — чаще половины. Система зарабатывает не частотой, а размером редких трендов.',
            tip: 'Классический выход — противоположный экстремум 10 дней. Такой бот собирается в конструкторе ботов.',
        },
        build: (o) => spec(o, 'Черепахи · 20 дней', mirror((side) => ({
            context: [],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(adx, number(20)), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 0),
    },
    {
        id: 'turtle-55',
        name: 'Черепахи · 55 дней',
        icon: '🐢',
        category: 'classic',
        hint: 'Вторая система Черепах: реже, но крупнее — пробой экстремума 55 баров.',
        steps: ['Пробит экстремум 55 баров', 'Тренд набирает силу'],
        keywords: ['черепахи', 'turtle', '55', 'дончиан', 'пробой'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '1D',
            idea: 'То же правило, что у 20-дневной системы, но окно длиннее: входит только в крупные, долгие тренды.',
            why: 'Пробой 55-дневного экстремума — редкое событие, и за ним чаще стоит настоящий тренд, а не колебание.',
            fails: 'Вход поздний: к моменту пробоя часть движения уже прошла. На коротком рынке сигналов нет месяцами.',
            tip: 'Классический выход — противоположный экстремум 20 дней.',
        },
        build: (o) => spec(o, 'Черепахи · 55 дней', mirror((side) => ({
            context: [],
            trigger: breakOut(side, 55),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 0),
    },
    {
        id: 'connors-rsi2',
        name: 'RSI-2 Коннорса',
        icon: '🎯',
        category: 'classic',
        popular: true,
        hint: 'Ларри Коннорс: в долгосрочном росте покупать короткую панику — RSI с периодом 2 падает ниже 10.',
        steps: ['Цена выше SMA 200 — долгий рост', 'RSI(2) упал ниже 10', 'Паника глубокая'],
        keywords: ['коннорс', 'connors', 'rsi 2', 'rsi2', 'откат'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '1D',
            idea: 'Пока рынок в долгом росте (выше средней 200), покупать двух-трёхдневные провалы — когда очень быстрый RSI уходит в крайность.',
            why: 'В растущем рынке короткие распродажи — это страх, а не смена тренда: цена часто быстро возвращается.',
            fails: 'Когда рынок ломает долгосрочный рост: «провал» оказывается началом падения. Поэтому фильтр SMA 200 обязателен.',
            tip: 'Классический выход у Коннорса — закрытие выше 5-дневной средней. Держать недолго, 2–5 дней.',
        },
        build: (o) => spec(o, 'RSI-2 Коннорса', mirror((side) => ({
            context: [side.beyond(close, sma(200))],
            trigger: side.sign > 0 ? crossDown(rsiOf(2), number(10)) : crossUp(rsiOf(2), number(90)),
            confirmations: [
                weight('c1', side.sign > 0 ? below(rsiOf(2), number(5)) : above(rsiOf(2), number(95)), 60),
                weight('c2', side.beyond(close, ema(50)), 40),
            ],
        })), 0),
    },
    {
        id: 'elder-three-screens',
        name: 'Три экрана Элдера',
        icon: '🖥',
        category: 'classic',
        popular: true,
        hint: 'Александр Элдер: старший таймфрейм задаёт тренд, средний ищет откат, вход — на пробое вчерашнего экстремума.',
        steps: ['Экран 1: дневной тренд по нашу сторону', 'Экран 2: Stochastic откатился в крайнюю зону', 'Экран 3: пробит экстремум прошлой свечи'],
        keywords: ['элдер', 'elder', 'три экрана', 'triple screen', 'мульти'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Первый экран — дневной тренд; второй — откат против него на рабочем таймфрейме; третий — момент, когда откат кончился.',
            why: 'Входить по старшему тренду на откате младшего — значит покупать дёшево в растущем рынке, а не догонять.',
            fails: 'Когда дневной тренд разворачивается: откат на младшем оказывается началом нового движения.',
            tip: 'Самая известная мульти-таймфреймовая система: хороший первый индикатор для того, кто освоил один таймфрейм.',
        },
        build: (o) => spec(o, 'Три экрана Элдера', mirror((side) => ({
            context: [
                onFrame(side.beyond(close, ema(26)), '1D'),
                side.sign > 0 ? below(previous(stochK, 1), number(30)) : above(previous(stochK, 1), number(70)),
            ],
            trigger: side.cross(close, previous(side.edge, 1)),
            confirmations: [weight('c1', onFrame(side.beyond(macdLine, macdSignal), '1D'), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'golden-cross',
        name: 'Золотой крест',
        icon: '✨',
        category: 'classic',
        hint: 'Простая средняя 50 пересекает среднюю 200: «золотой крест» вверх, «крест смерти» вниз.',
        steps: ['SMA 50 пересекла SMA 200', 'Цена по нужную сторону от SMA 200', 'Объём'],
        keywords: ['золотой крест', 'golden cross', 'крест смерти', 'death cross', 'sma'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '1D',
            idea: 'Самый известный сигнал смены долгого тренда: средняя за 50 дней пересекает среднюю за 200.',
            why: 'За пересечением следят фонды и новости — оно само притягивает деньги в сторону сигнала.',
            fails: 'Сигнал очень поздний и в боковике «крестов» бывает несколько подряд.',
            tip: 'Хорош как фильтр: торговать другими идеями только в сторону последнего креста.',
        },
        build: (o) => spec(o, 'Золотой крест', mirror((side) => ({
            context: [],
            trigger: side.cross(sma(50), sma(200)),
            confirmations: [weight('c1', side.beyond(close, sma(200)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'macd-200',
        name: 'MACD + EMA 200',
        icon: '📏',
        category: 'classic',
        hint: 'Популярная связка: по ходу EMA 200 брать пересечение MACD с сигнальной ниже нуля (для покупок).',
        steps: ['Цена по нужную сторону от EMA 200', 'MACD по ту сторону нуля — был откат', 'MACD пересёк сигнальную'],
        keywords: ['macd', 'ema 200', 'пересечение'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Тренд по EMA 200, внутри него откат — MACD ушёл ниже нуля — и разворот импульса обратно в сторону тренда.',
            why: 'Пересечение MACD ниже нуля в растущем рынке — это конец отката, а не случайное колебание наверху.',
            fails: 'В боковике около EMA 200 пересечения частые и ничего не значат.',
            tip: 'Не брать сигналы, если цена «лежит» на EMA 200 — нужна явная сторона.',
        },
        build: (o) => spec(o, 'MACD + EMA 200', mirror((side) => ({
            context: [side.beyond(close, ema(200)), side.sign > 0 ? below(macdLine, number(0)) : above(macdLine, number(0))],
            trigger: side.cross(macdLine, macdSignal),
            confirmations: [weight('c1', above(adx, number(20)), 50), weight('c2', above(rvol, number(1.1)), 50)],
        })), 0),
    },
    {
        id: 'bb-squeeze',
        name: 'Сжатие Боллинджера',
        icon: '🗜',
        category: 'classic',
        hint: 'Джон Картер: полосы Боллинджера сжались уже обычного размаха, затем цена вырвалась из них.',
        steps: ['Полосы были уже канала ATR — сжатие', 'Цена пробила полосу', 'Импульс и объём'],
        keywords: ['squeeze', 'сжатие', 'боллинджер', 'картер', 'ttm', 'келтнер'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Когда разброс цены падает ниже обычного размаха, полосы Боллинджера сжимаются внутрь канала ATR. Выход из сжатия — начало движения.',
            why: 'Низкая волатильность не длится вечно: за затишьем почти всегда следует расширение.',
            fails: 'Направление выхода угадать нельзя; первый выход бывает ложным.',
            tip: 'Смотрите на импульс (ROC): выход по его направлению надёжнее выхода против.',
        },
        build: (o) => spec(o, 'Сжатие Боллинджера', mirror((side) => ({
            context: [below(previous(ind('stddev', { period: 20 }), 2), scaled(atr(20), 0.75))],
            trigger: side.cross(close, bb(side.sign > 0 ? 'upper' : 'lower')),
            confirmations: [weight('c1', side.beyond(ind('roc', { period: 12 }), number(0)), 60), weight('c2', above(rvol, number(1.3)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=classic.js.map